import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Generic open/close/highlight/click-outside state for the tag picker's
 * browse-all dropdown — deliberately separate from useSearchableContextPicker
 * (the account/language pickers' hook) rather than extending it, so adding
 * arrow-key highlight navigation here can't regress those two pickers.
 */
export function useTagPickerDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const openDropdown = useCallback(() => {
    setIsOpen(true);
    setHighlightedIndex(0);
  }, []);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setHighlightedIndex(0);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handlePointerDown = (event: MouseEvent) => {
      if (
        containerRef.current
        && event.target instanceof Node
        && !containerRef.current.contains(event.target)
      ) {
        closeDropdown();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [closeDropdown, isOpen]);

  return {
    isOpen,
    openDropdown,
    closeDropdown,
    highlightedIndex,
    setHighlightedIndex,
    containerRef,
    inputRef,
  };
}
